import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import apiClient from '../services/apiClient';
import { showSessionExpiredNotice } from '../utils/sessionExpiredNotice';

const SESSION_WARNING_THRESHOLD_SECONDS = 900; // 15 minutes

/**
 * Gate booking navigation (details → passengers → payment) with the same
 * JWT session-validity check used on PaymentPage.
 */
export const useSessionValidityGate = (logPrefix = 'SessionValidity') => {
  const navigate = useNavigate();
  const [showSessionTimeoutWarning, setShowSessionTimeoutWarning] = useState(false);
  const [sessionValidity, setSessionValidity] = useState(null);
  const pendingResumeRef = useRef(null);
  const skipSessionCheckRef = useRef(false);
  const isCheckingRef = useRef(false);

  const checkSessionValidity = useCallback(async ({ onExpireSoon } = {}) => {
    if (skipSessionCheckRef.current) {
      console.log(`✅ [${logPrefix}] Skipping session check (token just refreshed)`);
      skipSessionCheckRef.current = false;
      return true;
    }

    try {
      console.log(`🔍 [${logPrefix}] Checking session validity...`);

      const response = await apiClient.get('/auth/session-validity');

      if (response.success === false) {
        console.warn(`⚠️ [${logPrefix}] Session validity check failed:`, response.message);
        navigate('/', { replace: true });
        showSessionExpiredNotice({ clearSession: true });
        return false;
      }

      if (!response.remaining_seconds && response.success !== true) {
        console.warn(`⚠️ [${logPrefix}] Invalid session response:`, response);
        toast.error('Failed to verify session. Please try again.');
        return false;
      }

      setSessionValidity(response);

      const totalRemainingSeconds = response.remaining_seconds || 0;
      console.log(`⏱️ [${logPrefix}] Session validity:`, {
        totalRemainingSeconds,
        formatted: response.remaining_time?.formatted
      });

      if (totalRemainingSeconds <= SESSION_WARNING_THRESHOLD_SECONDS) {
        console.log(`⚠️ [${logPrefix}] Session expiring soon (≤ 15 minutes). Showing warning modal.`);
        onExpireSoon?.();
        setShowSessionTimeoutWarning(true);
        return false;
      }

      console.log(`✅ [${logPrefix}] Session valid for more than 15 minutes. Proceeding.`);
      return true;
    } catch (error) {
      console.error(`❌ [${logPrefix}] Error checking session validity:`, error);

      if (error.status === 401) {
        navigate('/', { replace: true });
        showSessionExpiredNotice({ clearSession: true });
        return false;
      }

      toast.error('Failed to verify session. Please try again.');
      return false;
    }
  }, [logPrefix, navigate]);

  /**
   * Run session check, then resumeFn if valid. If session is expiring soon,
   * shows the warning modal and stores resumeFn until user stays signed in.
   */
  const gateProceed = useCallback(async (resumeFn, options = {}) => {
    if (isCheckingRef.current) {
      return;
    }

    isCheckingRef.current = true;
    try {
      pendingResumeRef.current = resumeFn;
      const valid = await checkSessionValidity(options);
      if (valid) {
        pendingResumeRef.current = null;
        await resumeFn();
      }
    } finally {
      isCheckingRef.current = false;
    }
  }, [checkSessionValidity]);

  const handleStaySignedInAndProceed = useCallback(async () => {
    console.log(`🔄 [${logPrefix}] Token refreshed. Resuming pending action...`);

    setShowSessionTimeoutWarning(false);
    setSessionValidity(null);
    skipSessionCheckRef.current = true;

    const resume = pendingResumeRef.current;
    pendingResumeRef.current = null;

    if (resume) {
      await resume();
    }
  }, [logPrefix]);

  const handleLogoutFromWarning = useCallback(() => {
    console.log(`🚪 [${logPrefix}] User logged out from session warning`);
    setShowSessionTimeoutWarning(false);
    setSessionValidity(null);
    pendingResumeRef.current = null;

    import('../utils/logout').then(({ performSimpleLogout }) => {
      performSimpleLogout();
    });
  }, [logPrefix]);

  return {
    showSessionTimeoutWarning,
    sessionValidity,
    gateProceed,
    handleStaySignedInAndProceed,
    handleLogoutFromWarning
  };
};

export default useSessionValidityGate;
